import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import StoryCard from '../components/ui/StoryCard';
import { stories } from '../data/stories';
import { VIDEOS } from '../lib/media';
import HeroVideo from '../components/HeroVideo';

export default function Stories() {
  return (
    <>
      {/* Hero */}
      <section className="page-hero">
        <HeroVideo className="page-hero-video" src={VIDEOS.meatGrill} />
        <div className="page-hero-overlay" />
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="section-label">Real People</span>
            <h1>Stories of Transformation</h1>
            <p>
              Composite stories drawn from thousands of real testimonials. The experiences, struggles, and transformations are all real.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Stories */}
      <section className="section">
        <div className="container">
          <div className="stories-centered-block">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="mb-lg">Why Stories Matter</h2>
              <p className="stories-body-text">
                Data can tell you what's possible. But stories show you what it feels like.
                They let you see yourself in someone else's journey—and believe that if they
                can transform, so can you.
              </p>
              <p className="stories-body-text">
                Each person below started where you might be now: frustrated, confused, and
                searching for answers. Their stories aren't just about changing how they eat—they're
                about finding simplicity and reclaiming their lives.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Range */}
      <section className="section section-light">
        <div className="container">
          <div className="stories-wide-block">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <h2 className="text-center mb-xl">Every Journey Is Different</h2>
              <div className="grid-3 gap-lg">
                <div className="stories-stat-card">
                  <p className="stories-stat-number">25-67</p>
                  <p className="color-text-light">Age Range</p>
                </div>
                <div className="stories-stat-card">
                  <p className="stories-stat-number">Diverse</p>
                  <p className="color-text-light">Backgrounds</p>
                </div>
                <div className="stories-stat-card">
                  <p className="stories-stat-number">Real</p>
                  <p className="color-text-light">Transformations</p>
                </div>
              </div>
              <p className="text-center mt-lg color-text-light">
                From busy professionals to retirees, from skeptics to enthusiasts—
                these stories show the full spectrum of people discovering carnivore.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Story Grid */}
      <section className="section">
        <div className="container">
          <h2 className="text-center mb-xl">Meet the People Who Transformed</h2>
          <div className="grid-auto">
            {stories.map((story, index) => (
              <StoryCard key={story.id} story={story} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Common Threads */}
      <section className="section section-light">
        <div className="container">
          <div className="stories-centered-block">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <h2 className="text-center mb-xl">What They Have In Common</h2>
              <div className="grid-stack">
                <div className="stories-common-card">
                  <p className="stories-common-text">
                    <strong>They were curious.</strong> They'd heard about carnivore and wondered if it could
                    work for them. They decided to find out.
                  </p>
                </div>
                <div className="stories-common-card">
                  <p className="stories-common-text">
                    <strong>They were skeptical.</strong> Could eating this simply really work?
                    But they were curious enough to try.
                  </p>
                </div>
                <div className="stories-common-card">
                  <p className="stories-common-text">
                    <strong>They found community.</strong> They didn't do it alone. They connected with others
                    who were on the same journey.
                  </p>
                </div>
                <div className="stories-common-card">
                  <p className="stories-common-text">
                    <strong>They kept going.</strong> The simplicity made it sustainable.
                    Carnivore became simply how they eat.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Your Story */}
      <section className="section">
        <div className="container">
          <div className="stories-narrow-block">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="mb-lg">Your Story Is Next</h2>
              <p className="stories-body-text">
                Every transformation in this book started with a single decision: to try something different.
                Not because it was easy or popular, but because nothing else had worked.
              </p>
              <p className="stories-body-text">
                You don't have to believe it will work for you. You just have to be willing to find out.
              </p>
              <p className="stories-closing-text">
                Thirty days from now, you could be writing your own transformation story.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>You Found Your Tribe</h2>
            <p>
              These people found their people. Now it's your turn.
              Join thousands who are thriving together.
            </p>
            <Link to="/join" className="btn btn-secondary btn-lg">
              Join the Community
            </Link>
            <p className="cta-footer-text">
              $97 one-time · Lifetime access
            </p>
          </motion.div>
        </div>
      </section>
    </>
  );
}
